import { TradingAccount, TradingAccountStatus } from '../proto/generated/trading-bridge'
import { formatAccountStatus, isPendingDematApplication } from '../pages/settings/tradingAccountProfile/helpers'
import {
    isBesseggenBrokerCode,
    isPaperBrokerCode,
    isPracticeTradeAccountEnabled
} from './tradingAccounts'

export type AccountSwitcherMode = 'no-accounts' | 'besseggen-icon' | 'switcher'

export const findPracticeAccount = (accounts: TradingAccount[]): TradingAccount | undefined =>
    accounts.find((account) => isPaperBrokerCode(account.brokerCode))

export const findBesseggenAccount = (accounts: TradingAccount[]): TradingAccount | undefined =>
    accounts.find((account) => isBesseggenBrokerCode(account.brokerCode))

export const isBesseggenAccountSwitchable = (status: TradingAccountStatus): boolean =>
    status === TradingAccountStatus.ACTIVE

export const isBesseggenAccountPending = (status: TradingAccountStatus): boolean =>
    isPendingDematApplication(status) ||
    status === TradingAccountStatus.DOCUMENTS_VERIFIED ||
    status === TradingAccountStatus.SYNC_PENDING ||
    status === TradingAccountStatus.SYNC_IN_PROGRESS

export const isBesseggenAccountSuspended = (status: TradingAccountStatus): boolean =>
    status === TradingAccountStatus.DISABLED ||
    status === TradingAccountStatus.BROKEN ||
    status === TradingAccountStatus.KYC_REJECTED ||
    status === TradingAccountStatus.SYNC_FAILED

export const formatBesseggenSwitcherStatusSuffix = (status: TradingAccountStatus): string => {
    if (isBesseggenAccountPending(status)) {
        return 'Pending review'
    }
    if (status === TradingAccountStatus.DISABLED) {
        return 'Suspended'
    }
    if (status === TradingAccountStatus.BROKEN) {
        return 'Disconnected'
    }
    if (status === TradingAccountStatus.KYC_REJECTED) {
        return 'Rejected'
    }
    return formatAccountStatus(status)
}

export const getBesseggenSwitchMenuItem = (account?: TradingAccount): { label: string; disabled: boolean } => {
    if (!account) {
        return { label: 'Switch to real trade', disabled: false }
    }
    if (isBesseggenAccountSwitchable(account.status)) {
        return { label: 'Switch to real trade', disabled: false }
    }
    const suffix = formatBesseggenSwitcherStatusSuffix(account.status)
    return { label: `Switch to real trade · ${suffix}`, disabled: true }
}

export const showSwitchToRealTradeOption = (
    practiceEnabled: boolean,
    besseggenAccount: TradingAccount | undefined
): boolean =>
    practiceEnabled && (!besseggenAccount || !isBesseggenAccountSwitchable(besseggenAccount.status))

/** User can place orders when practice trade is on or Besseggen demat is active. */
export const hasActiveTradingAccount = (
    practiceAccount: TradingAccount | undefined,
    besseggenAccount: TradingAccount | undefined
): boolean =>
    isPracticeTradeAccountEnabled(practiceAccount?.status) ||
    isBesseggenAccountSwitchable(besseggenAccount?.status ?? TradingAccountStatus.STATUS_UNKNOWN)

export const getAccountSwitcherMode = (
    practiceAccount: TradingAccount | undefined,
    besseggenAccount: TradingAccount | undefined
): AccountSwitcherMode => {
    const hasPracticeAccount = !!practiceAccount
    const hasBesseggenAccount = !!besseggenAccount
    const practiceEnabled = isPracticeTradeAccountEnabled(practiceAccount?.status)

    if (!hasPracticeAccount && !hasBesseggenAccount) {
        return 'no-accounts'
    }

    if (!practiceEnabled) {
        return 'besseggen-icon'
    }

    return 'switcher'
}

